import React, { memo, useEffect, useRef, useState } from 'react';
import { Monitor, Layers, Camera, Grid3x3 } from 'lucide-react';
import { NodeType } from '@/types';
import {
  AspectRatioGrid, ResolutionSelector, ParamSection,
  IMAGE_ASPECT_RATIOS, VIDEO_ASPECT_RATIOS, IMAGE_RESOLUTIONS, UPSCALE_RESOLUTIONS,
  VIDEO_RESOLUTIONS, IMAGE_COUNTS,
} from '../ParamSelectors';

interface AdvancedParamsEditorProps {
  nodeId: string;
  nodeType: NodeType;
  aspectRatio?: string;
  resolution?: string;
  duration?: number;
  imageCount?: number;
  videoCount?: number;
  upscaleStrength?: number;
  generateAudio?: boolean;
  generationMode?: string;
  modelSupportedResolutions?: string[];
  modelDurationRange?: { min: number; max: number; maxByResolution?: Record<string, number> };
  onUpdate: (id: string, data: any) => void;
}

// 选择窗点击弹出（对齐脚本节点底部面板下拉）：原 hover 式（group-hover 纯 CSS）鼠标掠过即弹出，改为点击切换；
// 外点关闭走 capture 阶段 mousedown（面板根 onMouseDown stopPropagation 会阻断冒泡，capture 先于 React 合成事件）
function useClickMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [open]);
  return { open, setOpen, ref };
}

const AdvancedParamsEditorInner: React.FC<AdvancedParamsEditorProps> = ({
  nodeId,
  nodeType,
  aspectRatio,
  resolution,
  duration,
  imageCount,
  videoCount,
  upscaleStrength,
  generateAudio,
  generationMode,
  modelSupportedResolutions = [],
  modelDurationRange,
  onUpdate,
}) => {
  const isUpscaleMode = generationMode === 'UPSCALE';
  const showImageCount = nodeType.includes('IMAGE') && !isUpscaleMode;

  // 四个选择窗各自独立开关（参数窗与张数窗可能同时可见，外点关闭互斥由 capture 监听自然保证）
  const imageParamsMenu = useClickMenu();
  const panoramaParamsMenu = useClickMenu();
  const videoParamsMenu = useClickMenu();
  const countMenu = useClickMenu();

  const getAvailableResolutions = (): string[] => {
    if (isUpscaleMode) return UPSCALE_RESOLUTIONS;
    if (nodeType.includes('VIDEO')) {
      // 模型声明了专属档位（如 minimax-h3 的 480p/768p/1080p）则按模型动态显示，未声明回退通用档位
      if (modelSupportedResolutions.length > 0) return modelSupportedResolutions;
      return VIDEO_RESOLUTIONS;
    }
    if (modelSupportedResolutions.length > 0) return modelSupportedResolutions;
    return IMAGE_RESOLUTIONS;
  };

  // 时长滑杆范围：模型声明了 durationRange（如 minimax-h3 的 1~15s）则按模型动态生效，且 maxByResolution 随分辨率收紧（1080p 上限 10s）；未声明回退通用 5~20s/步长 5
  const durationMin = modelDurationRange?.min ?? 5;
  const durationMax = modelDurationRange
    ? ((modelDurationRange.maxByResolution?.[resolution || ''] ?? modelDurationRange.maxByResolution?.[(resolution || '').toLowerCase()]) ?? modelDurationRange.max)
    : 20;
  const durationStep = modelDurationRange ? 1 : 5;
  const displayDuration = Math.min(Math.max(duration || 5, durationMin), durationMax);

  return (
    <>
      {nodeType === NodeType.IMAGE_GENERATOR && (
        <div className="relative" ref={imageParamsMenu.ref}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              imageParamsMenu.setOpen((v) => !v);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/5 cursor-pointer transition-colors text-xs font-medium text-white"
          >
            <Monitor size={12} />
            <span>{aspectRatio || '16:9'} · {(resolution || '1k').toUpperCase()}</span>
          </button>
          {imageParamsMenu.open && (
            <div className="absolute bottom-full left-0 pb-2 w-[260px] z-[200]">
              <div className="bg-[#262626] border border-white/10 rounded-xl shadow-xl overflow-hidden p-3">
                <ParamSection label="分辨率">
                  <ResolutionSelector resolutions={getAvailableResolutions()} allResolutions={IMAGE_RESOLUTIONS} activeResolution={resolution} onSelect={(r) => onUpdate(nodeId, { resolution: r })} />
                </ParamSection>
                <ParamSection label="比例" className="mb-0">
                  <AspectRatioGrid ratios={IMAGE_ASPECT_RATIOS} activeRatio={aspectRatio} onSelect={(r) => onUpdate(nodeId, { aspectRatio: r })} />
                </ParamSection>
              </div>
            </div>
          )}
        </div>
      )}

      {nodeType === NodeType.IMAGE_GENERATOR && (
        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/5 cursor-pointer transition-colors text-xs font-medium text-white" title="摄像机控制">
          <Camera size={12} />
          <span>摄像机控制</span>
        </button>
      )}

      {nodeType === NodeType.PANORAMA_GENERATOR && (
        <div className="relative" ref={panoramaParamsMenu.ref}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              panoramaParamsMenu.setOpen((v) => !v);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/5 cursor-pointer transition-colors text-xs font-medium text-white"
          >
            <Monitor size={12} />
            <span>{aspectRatio || '2:1'} · {(resolution || '1k').toUpperCase()}</span>
          </button>
          {panoramaParamsMenu.open && (
            <div className="absolute bottom-full left-0 pb-2 w-[260px] z-[200]">
              <div className="bg-[#262626] border border-white/10 rounded-xl shadow-xl overflow-hidden p-3">
                <ParamSection label="比例（全景固定 2:1）">
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => onUpdate(nodeId, { aspectRatio: '2:1' })}
                      className={`flex-1 py-1.5 rounded-lg text-[11px] font-medium transition-all border ${aspectRatio === '2:1' ? 'bg-teal-500/20 text-teal-400 border-teal-500/30' : 'text-white/50 border-white/10 hover:border-white/20 hover:bg-white/5'}`}
                    >
                      2:1（Equirectangular）
                    </button>
                  </div>
                </ParamSection>
                <ParamSection label="输出分辨率" className="mb-0">
                  <ResolutionSelector resolutions={IMAGE_RESOLUTIONS} allResolutions={IMAGE_RESOLUTIONS} activeResolution={resolution} onSelect={(r) => onUpdate(nodeId, { resolution: r })} />
                  <div className="mt-1.5 text-[9px] text-white/25 leading-relaxed">
                    1K≈1024×512 · 2K≈2048×1024 · 4K≈3840×1920
                  </div>
                </ParamSection>
              </div>
            </div>
          )}
        </div>
      )}

      {nodeType === NodeType.VIDEO_GENERATOR && (
        <div className="relative" ref={videoParamsMenu.ref}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              videoParamsMenu.setOpen((v) => !v);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/5 cursor-pointer transition-colors text-xs font-medium text-white"
          >
            <Monitor size={12} />
            <span>{aspectRatio || '16:9'} · {(resolution || '720p').toUpperCase()} · {duration || 5}s{generateAudio ? ' · 🔊' : ''}</span>
          </button>
          {videoParamsMenu.open && (
            <div className="absolute bottom-full left-0 pb-2 w-[260px] z-[200]">
              <div className="bg-[#262626] border border-white/10 rounded-xl shadow-xl overflow-hidden p-3">
                <ParamSection label="比例">
                  <AspectRatioGrid ratios={VIDEO_ASPECT_RATIOS} activeRatio={aspectRatio} onSelect={(r) => onUpdate(nodeId, { aspectRatio: r })} />
                </ParamSection>
                <ParamSection label="清晰度">
                  <ResolutionSelector resolutions={getAvailableResolutions()} activeResolution={resolution} onSelect={(r) => onUpdate(nodeId, { resolution: r })} />
                </ParamSection>
                <ParamSection label="视频时长">
                  <div className="flex items-center gap-3 px-1">
                    <input
                      type="range"
                      min={durationMin}
                      max={durationMax}
                      step={durationStep}
                      value={displayDuration}
                      onChange={(e) => onUpdate(nodeId, { duration: parseInt(e.target.value) })}
                      className="flex-1 h-1 bg-white/10 rounded-full appearance-none cursor-pointer accent-white"
                      style={{ accentColor: 'rgba(255,255,255,0.8)' }}
                    />
                    <span className="text-[11px] text-white/60 w-6 text-right">{displayDuration}s</span>
                  </div>
                </ParamSection>
                <ParamSection label="生成音频" className="mb-0">
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => onUpdate(nodeId, { generateAudio: true })}
                      className={`flex-1 py-1.5 rounded-lg text-[11px] font-medium transition-all border ${generateAudio === true ? 'bg-white/15 text-white border-white/30' : 'text-white/50 border-white/10 hover:border-white/20 hover:bg-white/5'}`}
                    >
                      开启
                    </button>
                    <button
                      onClick={() => onUpdate(nodeId, { generateAudio: false })}
                      className={`flex-1 py-1.5 rounded-lg text-[11px] font-medium transition-all border ${generateAudio !== true ? 'bg-white/15 text-white border-white/30' : 'text-white/50 border-white/10 hover:border-white/20 hover:bg-white/5'}`}
                    >
                      关闭
                    </button>
                  </div>
                </ParamSection>
              </div>
            </div>
          )}
        </div>
      )}

      {(showImageCount || nodeType === NodeType.VIDEO_GENERATOR || nodeType === NodeType.PANORAMA_GENERATOR) && (
        <div className="relative" ref={countMenu.ref}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              countMenu.setOpen((v) => !v);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/5 cursor-pointer transition-colors text-xs font-medium text-white"
          >
            <Layers size={12} />
            <span>{nodeType === NodeType.PANORAMA_GENERATOR ? `${imageCount || 1}张` : nodeType.includes('IMAGE') ? `${imageCount || 1}张` : `${videoCount || 1}个`}</span>
          </button>
          {countMenu.open && (
            <div className="absolute bottom-full left-0 pb-2 w-20 z-[200]">
              <div className="bg-[#262626] border border-white/10 rounded-xl shadow-xl overflow-hidden">
                {IMAGE_COUNTS.map(c => (
                  <div
                    key={c}
                    onClick={() => {
                      onUpdate(nodeId, { imageCount: c });
                      countMenu.setOpen(false);
                    }}
                    className={`px-3 py-2 text-xs font-medium cursor-default hover:bg-white/10 ${(imageCount || 1) === c ? 'text-white bg-white/5' : 'text-white/60'}`}
                  >
                    {`${c}张`}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {nodeType === NodeType.PANORAMA_GENERATOR && (
        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-500/20 border border-teal-500/30 text-teal-400 text-xs font-medium cursor-default">
          <Grid3x3 size={12} />
          <span>全景</span>
        </button>
      )}
    </>
  );
};

export const AdvancedParamsEditor = memo(AdvancedParamsEditorInner);
